<template>
    <ElDropdown v-bind="$attrs" ref="dropdownRef" trigger="click" :teleported="false">
        <div class="more-button">
            <slot v-if="slots.default" name="default" />
            <i v-else :class="'iconfont icon-xiala'"></i>
        </div>
        <template #dropdown>
            <ElDropdownMenu>
                <template v-if="isCurrentRoute()">
                    <ElDropdownItem @click="refresh">
                        <ElIcon><Refresh /></ElIcon>刷新
                    </ElDropdownItem>
                </template>
                <ElDropdownItem :divided="isCurrentRoute()" @click="closeCurrentTab">
                    <ElIcon><Remove /></ElIcon>关闭
                </ElDropdownItem>
                <ElDropdownItem @click="closeTabsOnSide(selectedRoute.fullPath ?? selectedRoute.path, 'left')">
                    <ElIcon><DArrowLeft /></ElIcon>关闭左侧
                </ElDropdownItem>
                <ElDropdownItem @click="closeTabsOnSide(selectedRoute.fullPath ?? selectedRoute.path, 'right')">
                    <ElIcon><DArrowRight /></ElIcon>关闭右侧
                </ElDropdownItem>
                <ElDropdownItem divided @click="closeOtherTab">
                    <ElIcon><CircleClose /></ElIcon>关闭其它
                </ElDropdownItem>
                <ElDropdownItem @click="closeAllTab">
                    <ElIcon><FolderDelete /></ElIcon>关闭所有
                </ElDropdownItem>
            </ElDropdownMenu>
        </template>
    </ElDropdown>
</template>

<script setup lang="ts">
import { inject, nextTick, ref, useSlots } from "vue";
import { useRoute, useRouter } from "vue-router";
import { ElDropdown, ElDropdownItem, ElDropdownMenu, ElIcon } from "element-plus";
import { CircleClose, DArrowLeft, DArrowRight, FolderDelete, Refresh, Remove } from "@element-plus/icons-vue";

import type { RouteLocationNormalizedLoaded } from "vue-router";

import { useTabsStore } from "@/stores/tabs";
import { useKeepAliveStore } from "@/stores/keepAlive";
import { HOME_URL } from "@/config";

const currentRoute = useRoute();
const selectedRoute = ref(currentRoute);
const router = useRouter();
const tabStore = useTabsStore();
const keepAliveStore = useKeepAliveStore();
const slots = useSlots();

const dropdownRef = ref<InstanceType<typeof ElDropdown> | null>(null);

// refresh current page
const refreshCurrentPage = inject<(isRefresh: boolean) => void>("refresh");
const refresh = () => {
    setTimeout(() => {
        keepAliveStore.removeKeepAliveName(selectedRoute.value.name as string);
        refreshCurrentPage?.(false);
        nextTick(() => {
            if (selectedRoute.value.meta.keepAlive) {
                keepAliveStore.addKeepAliveName(selectedRoute.value.name as string);
            }
            refreshCurrentPage?.(true);
        });
    }, 0);
};

// 关闭当前选中的tab
const closeCurrentTab = () => {
    if (selectedRoute.value.meta.isAffix) return;
    tabStore.removeTabs(
        selectedRoute.value.path,
        undefined,
        selectedRoute.value.name === currentRoute.matched[currentRoute.matched.length - 1].name
    );
    keepAliveStore.removeKeepAliveName(selectedRoute.value.name as string);
    // if (!keepAliveStore.keepAliveName.length) router.push(HOME_URL);
    if (tabStore.tabsMenuList.length === 0) {
        router.push(HOME_URL);
    }
};

// 关闭其它
const closeOtherTab = () => {
    tabStore.closeMultipleTab(selectedRoute.value.path);
    if (currentRoute.name !== selectedRoute.value.name) {
        tabStore.removeTabsByName(currentRoute.name as string, undefined, false, true);
        keepAliveStore.removeKeepAliveName(currentRoute.name as string);
        // 关闭当前，打开操作tab 页面
        router.push({ path: selectedRoute.value.path });
    }
};

// 关闭左侧 / 关闭右侧
const closeTabsOnSide = (path: string, type: "left" | "right") => {
    tabStore.closeTabsOnSide(path, type);
    // 选中的tab非当前开启的tab 打开选中的tab
    if (currentRoute.name !== selectedRoute.value.name) {
        router.push({ path: selectedRoute.value.path });
    }
};

// 关闭所有
const closeAllTab = () => {
    tabStore.closeAllTab();
    router.push(HOME_URL);
};

// tab右键菜单触发开启菜单
const handleOpen = ($event: Event) => {
    const event = $event as MouseEvent;
    if (!event?.target) return;
    const targetDom = event.target as HTMLElement;
    const id = targetDom?.id;
    const targetTabPath = id?.split("tab-")[1];
    // let collapseButtonWidth: number;
    // collapseButtonWidth = document.querySelector(".collapse-button")?.clientWidth || 0;
    selectedRoute.value = router.getRoutes()?.find(r => {
        const regex = new RegExp(`^${r.path.split(":")[0]}.*$`);
        if (id) {
            // 动态路由匹配
            if (r.path.includes(":")) {
                return regex.test(id?.split("tab-")[1]);
            } else if (targetTabPath?.includes("?")) {
                // 带query参数的路由对比？前部分
                const targetPath = targetTabPath.split("?")[0];
                return r.path.split("?")[0] === targetPath;
            } else {
                // 非动态路由， 非带query的路由，完全匹配path
                return r.path === targetTabPath;
            }
        }
        return false;
        // if (id) return regex.test(id?.split("tab-")[1]);
    }) as unknown as RouteLocationNormalizedLoaded;
    // 匹配路由后，如果是动态路由需将：id 替换成具体数据。2. 处理query 丢失的问题
    if (selectedRoute.value && (selectedRoute.value?.path.includes(":") || targetTabPath.includes("?"))) {
        selectedRoute.value.path = targetTabPath;
    }
    // console.log('selectedRoute-', selectedRoute.value);
    // ========== 先关闭上个弹框后再打开新的 ==========
    dropdownRef.value?.handleClose();
    // ========== dropdown位置，无论点击tab页签哪里，弹框都要在中间 =======
    const tabDom = id ? document.querySelector<HTMLElement>(`[id='${id}']`) : null;
    const asideWidth = document.querySelector(".aside-box")?.clientWidth ?? 0; // 获取 aside 宽度
    const headerHeight = document.querySelector(".layout-header")?.clientHeight ?? 0; // 获取 header 高度

    let positionY;
    let positionX;
    if (tabDom) {
        const tabRect = tabDom.getBoundingClientRect();
        const tabCenterX = tabRect.left + tabRect.width / 2;
        // 计算弹框位置
        positionY = tabRect.bottom - headerHeight - 16;
        positionX = tabCenterX - asideWidth - 8;
    } else {
        // 维持点击点的坐标
        positionY = event.clientY - headerHeight - 16;
        positionX = event.clientX - 8 - asideWidth;
    }

    const moreButtonDom = document.querySelector(".tab-more-button") as HTMLElement;
    if (!moreButtonDom) return;
    moreButtonDom.style.top = `${positionY}px`;
    moreButtonDom.style.left = `${positionX}px`;
    // 延迟再打开弹框，避免 handleClose 后立即 handleOpen 失效
    setTimeout(() => {
        dropdownRef.value?.handleOpen();
    }, 50); // 50ms 足够，太短可能无效，太长影响体验
};

const isCurrentRoute = () => {
    return currentRoute.name === selectedRoute.value.name;
};

defineExpose({
    handleOpen
});
</script>

<style scoped lang="scss">
@use "../index.scss";
</style>
